/**
 * text.js — what a person reads, out of what the book wrote for itself
 * =====================================================================
 * Usability review of 23 October 2026, item 5: *"Remove developer notes from
 * the screens ... ticket numbers like D-173 and D-098; lines like "UNPOSTED:
 * D-098…" in the deal activity."*
 *
 * The ledger's descriptions and a lot's notes are written by our own engines
 * and by whoever booked a correction, for the next person doing the same job:
 * they carry ticket numbers, the row ids the posting engines append ("— Lot
 * 1640"), machine tags ("[paydown:devs-…]") and internal references
 * ("ppln_cs_845"). None of those names anything in the world (CLAUDE.md 0.2),
 * so the page takes them off before printing. What is left is the sentence the
 * row was written to say, and nothing a reader needs to act on is removed: a
 * reversal still reads as a reversal, a retired lot is still marked as one —
 * by a badge on its row rather than a shouted prefix in its text.
 *
 * Display only. The records keep every word, and the Ledger page and the
 * deal's PDF are where the working notes can still be read in full.
 */

// The row ids our own posting engines append to a ledger description —
// "Stock sale net proceeds — Lot 1640", "initial investment (Lot 56)". The
// PDF's `without_row_ids` takes off the same two shapes.
const LOT_ID = /\s*(?:[—–-]\s*Lots? #?\d+\b|\(Lots? #?\d+\))/g;
export const withoutRowIds = (text) => (text || "").replace(LOT_ID, "").trim();

// A ticket number and the section after it: "D-098", "D-115 section 3",
// "D-141 §7", "D-196 §15".
const TICKET = String.raw`D-\d{2,4}(?:\s*(?:§|section)\s*[\d.]+)?`;

// In the order they run. Each is [pattern, replacement]; a pattern that
// leaves debris behind ("( ,", "— —") is tidied by TIDY below.
const STRIP = [
  // The unposter's and the corrector's prefixes. The row's badge says it.
  [/^\s*(?:\[[^\]]*\]\s*)*(?:UNPOSTED(?: and emptied)?|REVERSED)\s*(?:\([^)]*\))?\s*:\s*/i, ""],
  // Machine tags at the head of a lot's notes: "[D-098]", "[notice:287]",
  // "[paydown:devs-eloc-redemption-12-2025-07-21]", "[principal confirmed]".
  [/\[(?:D-\d+|[a-z][\w-]*:[^\]]*|principal confirmed)\]\s*/gi, ""],
  // A journal reference that is itself a ticket: "(JE D-093-devs-…)",
  // "(D-221-devs-itg-wire-fee-2026-01-16)".
  [new RegExp(String.raw`\(\s*(?:JE\s+)?D-\d+-[\w-]+\s*\)`, "g"), ""],
  [/\b(?:JE\s+)?D-\d+-[\w-]+/g, ""],
  // "(D-094)", "(D-115 section 3)": a bracket holding only the ticket.
  [new RegExp(String.raw`\s*\(\s*${TICKET}\s*\)`, "g"), ""],
  // "(D-096, 8 Sep 2026)", "(D-098: Valuno…": the ticket leads a bracket
  // that says something else too — keep the something else.
  [new RegExp(String.raw`\(\s*${TICKET}\s*[,:;]\s*`, "g"), "("],
  // "per D-101", "see D-070", "by D-101", "in D-196".
  [new RegExp(String.raw`\s*\b(?:per|see|by|in|from)\s+${TICKET}`, "g"), ""],
  // "D-093: " at the head of a clause, "D-241." as a sentence of its own,
  // "— D-143 —" between two dashes, and anything left.
  [new RegExp(String.raw`\b${TICKET}\s*[:.]\s*`, "g"), ""],
  [new RegExp(String.raw`\b${TICKET}\b`, "g"), ""],
  // The playbook, cited by section: "CLAUDE.md 0.12", "§0.46".
  [/CLAUDE\.md\s*§?\s*\d+(?:\.\d+)*/g, ""],
  [/§\s*\d+(?:\.\d+)*/g, ""],
  // Internal references and row ids: "ppln_cs_845", "fronted_cs_845's",
  // "deal_funding_backfill_291", "bf_citi:…", "TXN-B9232D8E58FD",
  // "register row 775", "notice 312", "lot 1752".
  [/'?\b(?:[a-z]+_)+(?:cs|backfill)_\d+(?:_v\d+)?\b(?:'s|')?/g, ""],
  [/\bbf_[\w:.-]+/g, ""],
  [/\bTXN-[0-9A-F]{6,}\b/g, ""],
  [/\b(?:register\s+)?row\s+\d+\b/gi, ""],
  [/\bnotice\s+\d+\b/gi, "notice"],
  [/(?:\s+(?:on|from))?\s+\b[Ll]ots?\s+#?\d+\b/g, ""],
  // A pipeline action named as the writer: "— ppln-bank-pass, register row
  // 602 —". The reader did not run it and cannot.
  [/\b(?:ppln-(?:bank-)?pass|post-(?:blotter|notices)|fetch-(?:fx|prices)|replay-conversions|set-note-principal|reverse-transaction|journal-entry|pay-down-note|book-purchase|redate-corrections)\b/g, ""],
  // A Drive file id read as a source: "(Drive 1Vmhd4M0gSIH3jAZRqlQ_PJT7…)".
  [/\bDrive\s+[A-Za-z0-9_-]{20,}[;,]?\s*/g, ""],
  // A broker's own trade key: "CLEAR_STREET 60202-cfedffa77ef07a3aee0ae5bc4a |".
  [/\b[A-Z_]{3,}\s+\d+-[0-9a-f]{16,}\s*\|?\s*/g, ""],
];

// The debris a removal leaves.
const TIDY = [
  [/([—–])\s*[,;:]\s*/g, "$1 "],
  [/\s*[—–]\s*\)/g, ")"],
  [/\(\s*[,;:]\s*/g, "("],
  [/\s*[,;:]\s*\)/g, ")"],
  [/\(\s*\)/g, ""],
  [/''/g, ""],
  [/\s+([,.;:)])/g, "$1"],
  [/\(\s+/g, "("],
  [/([—–])\s*[—–]/g, "$1"],
  [/\s*[—–]\s*$/g, ""],
  [/^\s*[—–:,.;|]\s*/g, ""],
  [/\|\s*$/g, ""],
  [/\.{2,}/g, "."],
  [/\s{2,}/g, " "],
];

// The ledger's shouting, in sentence case: "REVERSAL (" reads "Reversal (",
// "RE-DATED into 2026-07:" reads "Re-dated into 2026-07:".
const SHOUT = [
  [/\bREVERSAL\b/g, "Reversal"],
  [/\bRE-DATED\b/g, "Re-dated"],
  [/\bUNPOSTED\b/g, "unposted"],
  [/\bREVERSED\b/g, "reversed"],
];

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * A free-text field from the book, as a person should read it. `dealName`,
 * when given, comes off the end of a description that closes on it — "… —
 * Devvstream Crypto Convertible Note" — because on that deal's own page it
 * says nothing the heading has not.
 */
export function readerText(text, { dealName } = {}) {
  if (!text) return "";
  let out = String(text).replace(LOT_ID, "");
  if (dealName) {
    out = out.replace(new RegExp(`\\s*[\u2014\u2013-]\\s*${escapeRegExp(dealName)}\\s*$`), "");
  }
  STRIP.forEach(([pattern, replacement]) => {
    out = out.replace(pattern, replacement);
  });
  SHOUT.forEach(([pattern, replacement]) => {
    out = out.replace(pattern, replacement);
  });
  // Twice: one tidy can expose another ("( ,)" -> "(,)" -> "").
  const tidy = (text) => TIDY.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), text);
  return tidy(tidy(out)).trim();
}

/** A lot the unposter or a correction retired: zeroed or re-booked, kept for the record. */
export function isRetiredNote(notes) {
  // "[summary-unwound:…] emptied — this row carried a hand-entered deal
  // summary" is the third spelling of the same state.
  return /^\s*(?:\[[^\]]*\]\s*)*(?:UNPOSTED|REVERSED|emptied)\b/i.test(notes || "");
}
